import classnames from 'classnames';
import { memo, useEffect, useRef, useState } from 'react';
import translations from '../../../translations';
import type { Environment as EnvironmentType } from '../../../types/environment';
import { isNullOrEmpty } from '../../../utils/guard';
import ButtonPrimary from '../../buttons/ButtonPrimary';
import Loader from '../../loader/Loader';
import { useTabsDispatch } from '../../tabs/TabsProvider';
import EnvironmentFlows from './EnvironmentFlows';
import EnvironmentSettings from './EnvironmentSettings';
import { useEnvironments } from './EnvironmentsProvider';
import EnvironmentThemes from './EnvironmentThemes';
import EnvironmentVariables from './EnvironmentVariables';
import IntegrationEnvironments from './IntegrationEnvironments';
import ReleasesList from './ReleasesList';

type Props = {
    tabKey: string;
    environmentId: string;
    isActive: boolean;
};

const Environment = ({ tabKey, environmentId, isActive }: Props) => {
    const [environment, setEnvironment] = useState<EnvironmentType | null>(null);
    const [activeTab, setActiveTab] = useState('releases');
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const modalContainerRef = useRef<HTMLDivElement>(null);
    const dispatchTabs = useTabsDispatch();

    const { environmentsLoading, getAllEnvironments, saveEnvironment, environments } =
        useEnvironments();

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to add all dependencies
    useEffect(() => {
        const loadEnvironments = async () => {
            let envs = environments;
            if (isNullOrEmpty(envs)) {
                envs = await getAllEnvironments();
                if (envs) {
                    const env = Object.values(envs).find(
                        (environment) => environment.id === environmentId,
                    );
                    setEnvironment(env || null);

                    if (env) {
                        dispatchTabs({
                            type: 'SET_TITLE',
                            payload: {
                                tabKey,
                                title: env.name,
                            },
                        });
                    }
                }
            }
        };

        loadEnvironments();
    }, []);

    const save = (environment: EnvironmentType | null) => {
        setHasSubmitted(true);
        if (environment && isValid(environment)) {
            saveEnvironment(environment);
        }
    };

    const isValid = (environment: EnvironmentType) => {
        if (isNullOrEmpty(environment.name)) {
            return false;
        }
        if (isNullOrEmpty(environment.defaultPlayerName)) {
            return false;
        }
        if (isNullOrEmpty(environment.defaultThemeName)) {
            return false;
        }

        return true;
    };

    const updateEnvironment = (environment: EnvironmentType) => {
        setEnvironment(environment);
    };

    const environmentTabs = (
        <ul className="nav nav-tabs">
            <li className={classnames({ active: activeTab === 'releases' })}>
                <button
                    className="nav-tab"
                    onClick={() => setActiveTab('releases')}
                    title={translations.ENVIRONMENT_releases_tab_title}
                    role="tab"
                    name="Releases"
                    type="button"
                >
                    Releases
                </button>
            </li>
            <li className={classnames({ active: activeTab === 'flows' })}>
                <button
                    className="nav-tab"
                    onClick={() => setActiveTab('flows')}
                    title={translations.ENVIRONMENT_flows_tab_title}
                    role="tab"
                    name="Flows"
                    type="button"
                >
                    Flows
                </button>
            </li>
            <li className={classnames({ active: activeTab === 'themes' })}>
                <button
                    className="nav-tab"
                    onClick={() => setActiveTab('themes')}
                    title={translations.ENVIRONMENT_themes_tab_title}
                    role="tab"
                    name="Themes"
                    type="button"
                >
                    Themes
                </button>
            </li>
            <li className={classnames({ active: activeTab === 'variables' })}>
                <button
                    className="nav-tab"
                    onClick={() => setActiveTab('variables')}
                    title={translations.ENVIRONMENT_variable_tab_title}
                    role="tab"
                    name="Variables"
                    type="button"
                >
                    Variables
                </button>
            </li>
            <li className={classnames({ active: activeTab === 'settings' })}>
                <button
                    className="nav-tab"
                    onClick={() => setActiveTab('settings')}
                    title={translations.ENVIRONMENT_settings_tab_title}
                    role="tab"
                    name="Settings"
                    type="button"
                >
                    Settings
                </button>
            </li>
            <li className={classnames({ active: activeTab === 'integrationAccounts' })}>
                <button
                    className="nav-tab"
                    onClick={() => setActiveTab('integrationAccounts')}
                    title={translations.ENVIRONMENT_integration_accounts_tab_title}
                    role="tab"
                    name="Integration Accounts"
                    type="button"
                >
                    Integration Accounts
                </button>
            </li>
        </ul>
    );

    return (
        <div ref={modalContainerRef} className="page-wrapper">
            <div className="admin-page">
                <h1>{!environmentsLoading && environment ? environment.name : ''}</h1>
                {environmentTabs}
                {(() => {
                    if (activeTab === 'releases') {
                        if (environmentsLoading) {
                            return <Loader message="Loading environment" />;
                        }
                        if (environment) {
                            return (
                                <ReleasesList
                                    passedEnvironments={environments}
                                    environmentId={environment.id}
                                    isActive={isActive}
                                />
                            );
                        }
                    } else if (activeTab === 'flows') {
                        return <EnvironmentFlows environment={environment} />;
                    } else if (activeTab === 'themes') {
                        return <EnvironmentThemes environment={environment} />;
                    } else if (activeTab === 'variables' && environment) {
                        return (
                            <EnvironmentVariables
                                environmentId={environment?.id}
                                modalContainerRef={modalContainerRef}
                                isActive={isActive}
                            />
                        );
                    } else if (activeTab === 'settings') {
                        return (
                            <EnvironmentSettings
                                environment={environment}
                                updateEnvironment={updateEnvironment}
                                hasSubmitted={hasSubmitted}
                            />
                        );
                    } else if (activeTab === 'integrationAccounts') {
                        return (
                            <IntegrationEnvironments
                                environment={environment}
                                updateEnvironment={updateEnvironment}
                            />
                        );
                    }
                    return null;
                })()}
            </div>
            {activeTab === 'settings' || activeTab === 'integrationAccounts' ? (
                <div className="flex column-footer padding-default border-top config-footer">
                    <ButtonPrimary
                        title={translations.ENVIRONMENT_save_button_title}
                        onClick={() => save(environment)}
                        className="flex-child-right"
                    >
                        {translations.ENVIRONMENT_save}
                    </ButtonPrimary>
                </div>
            ) : null}
        </div>
    );
};

export default memo(Environment);
